<template>
  <app-modal
    v-model="isDialogOpened"
    :size="dialogMaxSize"
    data-locator="group-add-member-dialog"
    @cancel="closeDialog"
  >
    <template #title>
      {{ $t('project.members.dialogs.GroupAddMemberDialog.title') }}
    </template>

    <template #body>
      <AddMemberForm v-if="dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.MEMBER_FORM"
                     ref="addMemberForm"
                     v-model:memberForm="memberForm"
                     :group="group"
                     @previewInvitation="dialogContent = GROUP_ADD_MEMBER_DIALOG_CONTENT.INVITATION_PREVIEW"
                     @change-add-member-type="addMemberType = $event"
                     @onValidate="onValidate"
      />

      <MemberInvitationPreview v-if="dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.INVITATION_PREVIEW"
                               :memberForm="memberForm"
      />

      <MemberSubscription v-if="dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.MEMBER_SUBSCRIPTION"
                          ref="memberSubscription"
                          :memberForm="memberForm"
                          :errorData="memberSubscriptionErrorData"
                          @update:okDisabled="subscriptionOkDisabled = $event"
                          @onSubscriptionDecline="cancelUser"
                          @onSubscriptionValidate="preparePostMember"
      />

      <template v-if="dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.IRRECOVERABLE_ERROR">
        <div>
          {{
            $t('project.members.dialogs.GroupImportDialog.error.ERR_FORBIDDEN_INTRO', {
              'subName': subName,
              'user': (memberForm.firstName + ' ' + memberForm.lastName),
            })
          }}
        </div>
        <div v-if="errorCode === 'ERR_FORBIDDEN_FOR_NON_SUB_USER'" class="mt-2">
          {{
            $t('project.members.dialogs.GroupImportDialog.error.ERR_FORBIDDEN_FOR_NON_SUB_USER', {
              'subName': subName,
              'user': (memberForm.firstName + ' ' + memberForm.lastName),
            })
          }}
        </div>
        <div v-if="errorCode === 'ERR_FORBIDDEN_FOR_NON_SUB_ADMIN'" class="mt-2">
          {{
            $t('project.members.dialogs.GroupImportDialog.error.ERR_FORBIDDEN_FOR_NON_SUB_ADMIN', {
              'subName': subName,
              'user': (memberForm.firstName + ' ' + memberForm.lastName),
            })
          }}
          <div class="mt-2">
            <div v-for="admin in subAdminContacts" :key="admin.email">- {{ admin.fullName }} ({{ admin.email }})</div>
          </div>
        </div>
      </template>
    </template>

    <template #footer>
      <v-spacer />

      <!-- These two steps used to be left through a close icon that actually meant "go back",
           indistinguishable from closing the dialog. -->
      <app-button v-if="canGoBackToForm"
                  type="outlined"
                  variant="neutral"
                  data-locator="back-button"
                  @click="dialogContent = GROUP_ADD_MEMBER_DIALOG_CONTENT.MEMBER_FORM"
      >
        {{ $t('common.back') }}
      </app-button>

      <template v-if="dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.MEMBER_FORM">
        <app-button type="outlined" variant="neutral" data-locator="cancel-button" @click="closeDialog">
          {{ $t('common.cancel') }}
        </app-button>
        <app-button :loading="postMemberIsInProgress || postMemberFromTeamLoading"
                    data-locator="confirm-button"
                    @click="$refs.addMemberForm.submitForm()"
        >
          {{ $t('common.add') }}
        </app-button>
      </template>

      <app-button v-else-if="dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.MEMBER_SUBSCRIPTION"
                  :disabled="subscriptionOkDisabled"
                  :loading="postMemberIsInProgress"
                  data-locator="confirm-button"
                  @click="$refs.memberSubscription.validate()"
      >
        {{ $t('common.ok') }}
      </app-button>

      <app-button v-else-if="dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.IRRECOVERABLE_ERROR"
                  type="outlined"
                  variant="neutral"
                  data-locator="cancel-button"
                  @click="closeDialog"
      >
        {{ $t('common.cancel') }}
      </app-button>
    </template>
  </app-modal>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

import { track } from '@/common/pendo/agent'
import { PRODUCT_ROOM_TYPES } from '@/common/utils/roomTypes'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { MemberFormModel } from '@/models/memberForm.model'

import AddMemberForm from '../AddMemberForm.vue'
import MemberInvitationPreview from '../MemberInvitationPreview.vue'
import MemberSubscription from '../MemberSubscription.vue'
import { MEMBER_TYPE_OPTIONS } from '../types.ts'

const GROUP_ADD_MEMBER_DIALOG_CONTENT = {
  MEMBER_FORM: 'member-form',
  INVITATION_PREVIEW: 'invitation-preview',
  MEMBER_SUBSCRIPTION: 'member-subscription',
  IRRECOVERABLE_ERROR: 'irrecoverable-error',
}

export default {
  name: 'GroupAddMemberDialog',
  components: {
    AddMemberForm,
    MemberInvitationPreview,
    MemberSubscription,
  },
  mixins: [dialogMixin],
  props: {
    group: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      MEMBER_TYPE_OPTIONS,
      addMemberType: MEMBER_TYPE_OPTIONS.NEW_MEMBER,
      GROUP_ADD_MEMBER_DIALOG_CONTENT,
      dialogContent: GROUP_ADD_MEMBER_DIALOG_CONTENT.MEMBER_FORM,
      errorCode: null,
      memberForm: new MemberFormModel(),
      memberSubscriptionErrorData: null,
      postMemberIsInProgress: false,
      subscriptionOkDisabled: true,
      subAdminContacts: null,
      subName: null,
      visibility: true,
    }
  },
  computed: {
    ...mapState('user', ['profile']),
    ...mapState('groups', ['groups', 'postMemberFromTeamLoading']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['roomMnemo']),
    isPreviewStep () {
      return this.dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.INVITATION_PREVIEW
    },
    dialogMaxSize () {
      return this.isPreviewStep ? 'lg' : 'md'
    },
    canGoBackToForm () {
      return this.dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.INVITATION_PREVIEW ||
        this.dialogContent === GROUP_ADD_MEMBER_DIALOG_CONTENT.MEMBER_SUBSCRIPTION
    },
  },
  mounted () {
    if (this.profile.locale) {
      this.memberForm.locale = this.profile.locale
    }
  },
  methods: {
    ...mapActions('groups', ['POST_MEMBER', 'GET_GROUPS', 'POST_USER_MISSED_ACCESS', 'POST_MEMBER_FROM_TEAM']),
    async cancelUser () {
      await this.POST_USER_MISSED_ACCESS({
        mnemo: this.roomMnemo,
        data: { email: this.memberForm.email },
      })
      this.closeDialog()
    },
    onValidate () {
      if (this.addMemberType === MEMBER_TYPE_OPTIONS.NEW_MEMBER) {
        this.preparePostMember()
      } else {
        this.preparePostMemberFromTeam()
      }
    },
    async preparePostMember (params) {
      this.postMemberIsInProgress = true

      try {
        const {
          askForIdCheck,
          customInvitationMessage,
          projectManagersAreHidden,
          sendInvitationNow,
          withCustomInvitationMessage,
          ...memberData
        } = this.memberForm

        const data = {
          ...memberData,
          groupId: this.group.id,
          sendInvitationNow: sendInvitationNow,
          idVerification: askForIdCheck,
          hidePm: projectManagersAreHidden,
        }

        if (withCustomInvitationMessage) {
          data.customMessage = customInvitationMessage
        }

        await this.POST_MEMBER({
          data,
          params,
        })

        toast.success(`${this.memberForm.firstName} ${this.memberForm.lastName} ${this.$t('project.members.dialogs.GroupAddMemberDialog.userAddedSuccess')}`)
        track('user_added', {
          project_mnemo: this.roomMnemo,
          project_type: this.currentRoom.isDataroom ? PRODUCT_ROOM_TYPES.DATAROOM : PRODUCT_ROOM_TYPES.LEGAL_PROJECT,
          user_role: this.isCurrentUserPm ? MEMBER_TYPE_OPTIONS.PROJECT_MANAGER : MEMBER_TYPE_OPTIONS.GUEST,
        })
        this.GET_GROUPS(this.roomMnemo)
        this.closeDialog()
      } catch (error) {
        if (error.response?.status === 300) {
          if (error.response?.data) {
            this.memberSubscriptionErrorData = error.response.data
            this.dialogContent = GROUP_ADD_MEMBER_DIALOG_CONTENT.MEMBER_SUBSCRIPTION
          }
        } else if (error.response?.data?.errorCode) {
          switch (error.response.data.errorCode) {
            case 'ERR_USER_ALREADY_IN_ROOM':
            case 'ERR_INVALID_INPUT': {
              toast.error(this.$t(`project.members.dialogs.GroupAddMemberDialog.errors.${error.response.data.errorCode}`))
              break
            }
            case 'ERR_FORBIDDEN_FOR_NON_SUB_USER': {
              this.errorCode = 'ERR_FORBIDDEN_FOR_NON_SUB_USER'
              this.dialogContent = GROUP_ADD_MEMBER_DIALOG_CONTENT.IRRECOVERABLE_ERROR
              this.subName = error.response.data.errorContext?.subName
              break
            }
            case 'ERR_FORBIDDEN_FOR_NON_SUB_ADMIN': {
              this.errorCode = 'ERR_FORBIDDEN_FOR_NON_SUB_ADMIN'
              this.dialogContent = GROUP_ADD_MEMBER_DIALOG_CONTENT.IRRECOVERABLE_ERROR
              this.subName = error.response.data.errorContext?.subName
              this.subAdminContacts = error.response.data.errorContext?.subAdmins
              break
            }
          }
        } else {
          toast.error(this.$t('common.msgFailErrorOccurred'))
        }
      } finally {
        this.postMemberIsInProgress = false
      }
    },
    async preparePostMemberFromTeam () {
      const {
        sendInvitationNow,
        askForIdCheck,
        withCustomInvitationMessage,
        customInvitationMessage,
        team,
      } = this.memberForm

      try {
        const response = await this.POST_MEMBER_FROM_TEAM({
          mnemo: this.roomMnemo,
          data: {
            groupId: this.group.id,
            teamId: team.id,
            sendInvitationNow: sendInvitationNow,
            idVerification: askForIdCheck,
            customMessage: withCustomInvitationMessage ? customInvitationMessage : '',
          },
        })
        if (response?.data?.someUsersAlreadyInRoom) {
          toast.warning(this.$t('project.members.AddMemberFromTeam.someUsersAlreadyInRoom'))
        } else {
          toast.success(this.$t('project.members.AddMemberFromTeam.addParticipantsSuccess', { groupName: this.group.name }))
        }
        this.GET_GROUPS(this.roomMnemo)
        this.closeDialog()
      } catch {
        toast.error(this.$t('project.members.AddMemberFromTeam.addParticipantsError'))
      }
    },
  },
}
</script>
